import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import StbButton from "../../atoms/button/StbButton";
import "./CreateProject.css";
import CreateProjectBriljantIntegration from "../../organisms/CreateProject/CreateProjectBriljantIntegration";
import CreateProjectByggkollenIntegration from "../../organisms/CreateProject/CreateProjectByggkollenIntegration";
import CreateProjectOfflineForm from "../../organisms/CreateProject/CreateProjectOfflineForm";
import ModalAddContact from "../../organisms/CreateProject/ModalContact/ModalAddContact";
import ModalEditContact from "../../organisms/CreateProject/ModalContact/ModalEditContact";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useGetApiContactsCustomerCustomerNumber } from "../../../api/generated/endpoints/contact/contact";
import {
  useGetApiErpImportGetInitialProjectInformation,
  useGetApiErpImportGetInitialProjectInformationByReference,
} from "../../../api/generated/endpoints/erp-import/erp-import";
import type { ContactSummaryDto } from "../../../api/generated/model";
import LoadingSection from "../../atoms/loadingsection/LoadingSection";
import { CloseCircleOutlined } from "@ant-design/icons";
import CreateProjectForm from "../../organisms/CreateProject/CreateProjectForm";

const CreateProject: React.FC = () => {
  const [selectedProjectNumber, setSelectedProjectNumber] = useState<string | undefined>();
  const [erpReference, setErpReference] = useState<string | undefined>();
  const [department, setDepartment] = useState<
    number | undefined
  >();
  const [maunallyCreateProject, setMaunallyCreateProject] =
    useState<boolean>(false);
  const [isModalAddContactVisible, setIsModalAddContactVisible] =
    useState<boolean>(false);
  const [isModalEditContactVisible, setIsModalEditContactVisible] =
    useState<boolean>(false);
  const [customerNumber, setCustomerNumber] = useState<number | undefined>();
  const [isContactListUpdated, setIsContactListUpdated] =
    useState<boolean>(false);
  const [addContactErrorMessage, setAddContactErrorMessage] =
    useState<string>("");
  const [showConfirmationMessage, setShowConfirmationMessage] =
    useState<boolean>(false);
  const [customerContactList, setCustomerContactList] = useState<ContactSummaryDto[]>([]);
  const [selectedContactsIds, setSelectedContactsIds] = useState<number[]>([]);

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const erpIntegration = (process.env.REACT_APP_ERP_INTEGRATION || "briljant").toLowerCase();
  const isByggkollenIntegration = erpIntegration === "byggkollen";
  const integrationLabel = isByggkollenIntegration ? "Byggkollen" : "Briljant";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const projectNumberParam = params.get("projectNumber");
    const erpReferenceParam = params.get("erpReference");
    const manualParam = params.get("manual");
    const departmentParam = params.get("department");

    if (manualParam === "true") {
      setMaunallyCreateProject(true);
      setSelectedProjectNumber(undefined);
      setErpReference(undefined);
      setDepartment(undefined);
      setSelectedContactsIds([]); 
      setCustomerContactList([]); 
      setCustomerNumber(undefined); 
    } else {
      setMaunallyCreateProject(false);
      // Reset contact selection when project query params change to avoid
      // carrying over stale selected IDs from a previously loaded project.
      setSelectedContactsIds([]);
      setCustomerContactList([]);
      setCustomerNumber(undefined);

      if (projectNumberParam) {
        setSelectedProjectNumber(projectNumberParam);
      } else {
        setSelectedProjectNumber(undefined);
      }

      if (erpReferenceParam) {
        setErpReference(erpReferenceParam);
      } else {
        setErpReference(undefined);
      }
  
      if (departmentParam) {
        setDepartment(Number(departmentParam));
      } else {
        setDepartment(undefined);
      }
    }
  }, [location.search]);

  const hasAnyQueryParam = !!selectedProjectNumber || !!erpReference || !!department;
  const hasSelectedDepartmentAndProject = !!selectedProjectNumber || !!erpReference;
  const isProjectSelectionComplete = isByggkollenIntegration
    ? hasAnyQueryParam
    : hasSelectedDepartmentAndProject;

  const hasOnlyErpReference = !!erpReference && !selectedProjectNumber;
  
  // Query for project by ERP reference
  const { 
    data: projectDataByErpRef, 
    error: projectErrorByErpRef, 
    isLoading: isProjectLoadingByErpRef 
  } = useGetApiErpImportGetInitialProjectInformationByReference(
    hasOnlyErpReference && erpReference ? { erpProjectRef: erpReference } : undefined,
    {
      query: {
        enabled: hasOnlyErpReference && !!erpReference && isProjectSelectionComplete && !maunallyCreateProject,
        staleTime: Infinity,
      },
    }
  );
  
  // Query for project by project number
  const { 
    data: projectDataByAo, 
    error: projectErrorByAo, 
    isLoading: isProjectLoadingByAo 
  } = useGetApiErpImportGetInitialProjectInformation(
    !hasOnlyErpReference && (selectedProjectNumber || erpReference) 
      ? { 
          ProjectNumber: selectedProjectNumber?.trim(),
          erpReference: erpReference,
        } 
      : undefined,
    {
      query: {
        enabled: !hasOnlyErpReference && isProjectSelectionComplete && !maunallyCreateProject,
        staleTime: Infinity,
      },
    }
  );
  
  // Combine results
  const projectData = projectDataByErpRef || projectDataByAo;
  const projectError = projectErrorByErpRef || projectErrorByAo;
  const isProjectLoading = isProjectLoadingByErpRef || isProjectLoadingByAo;

  useEffect(() => {
    if (!maunallyCreateProject && !isProjectLoading && projectData && !projectError) {
      setCustomerNumber(projectData.customerNumber ?? undefined);
    }
    if (maunallyCreateProject || !isProjectSelectionComplete) {
        setCustomerNumber(undefined);
    }
  }, [maunallyCreateProject, isProjectLoading, projectData, projectError, isProjectSelectionComplete, setCustomerNumber]);

  // Query for contacts by customer number
  const { 
    data: fetchedContacts, 
  } = useGetApiContactsCustomerCustomerNumber(
    customerNumber || 0,
    {
      query: {
        enabled: !!customerNumber && !maunallyCreateProject,
      },
    }
  );

  useEffect(() => {
    if (!customerNumber || maunallyCreateProject) { 
        setCustomerContactList([]); 
        return;
    }
    
    if (fetchedContacts) {
      setCustomerContactList(fetchedContacts);
      setIsContactListUpdated(false);
    }
  }, [customerNumber, maunallyCreateProject, fetchedContacts, isContactListUpdated]);

  const goBack = () => {
    setMaunallyCreateProject(false);
    setSelectedProjectNumber(undefined);
    setErpReference(undefined);
    setDepartment(undefined);
    navigate("/createproject");
  };

  const handleCreateModeSwitchChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isManual = e.currentTarget.checked;
    setMaunallyCreateProject(isManual);
    setSelectedProjectNumber(undefined);
    setErpReference(undefined);
    setDepartment(undefined);
    setSelectedContactsIds([]);
    setCustomerContactList([]);
    setCustomerNumber(undefined);

    if (!isManual) {
      navigate("/createproject", { replace: true });
    }
  };

  // Render Loading state
  if (!maunallyCreateProject && isProjectSelectionComplete && isProjectLoading) {
    return (
      <Container
        fluid
        className="mt-5 pt-5 d-flex justify-content-center align-items-center vstack"
      >
        <LoadingSection text={`Hämtar data från ${integrationLabel}..`} />
        <Row className="justify-content-center align-items-center mt-2">
          <StbButton
            variant="secondary"
            className="justify-content-center"
            onClick={goBack}
          >
            Avbryt{" "}
            <CloseCircleOutlined aria-hidden="true" className="justify-content-center align-items-center" />
          </StbButton>
        </Row>
      </Container>
    );
  }

  // Render Error state
  if (!maunallyCreateProject && isProjectSelectionComplete && projectError) {
    return (
      <Container fluid className="mt-3 d-flex justify-content-center">
        <span>
          <h2>Error: {projectError.message}</h2>
          <StbButton variant="secondary" onClick={goBack} className="ms-3">
            Tillbaka
          </StbButton>
        </span>
      </Container>
    );
  }

  // Determine which form to render
  let formToRender = null;
  if (maunallyCreateProject) {
    // Using CreateProjectForm for offline mode too, with offlineProjectData
    formToRender = <CreateProjectOfflineForm />;
  } else if (isProjectSelectionComplete && projectData) {
    // Briljant mode, data loaded
    formToRender = (
      <CreateProjectForm
        department={projectData.department ?? undefined} 
        selectedProjectNumber={projectData.projectNumber ?? undefined} 
        isModalAddContactVisible={isModalAddContactVisible}
        setIsModalAddContactVisible={setIsModalAddContactVisible}
        setCustomerNumber={setCustomerNumber}
        isContactListUpdated={isContactListUpdated}
        setIsContactListUpdated={setIsContactListUpdated}
        addContactErrorMessage={addContactErrorMessage}
        showConfirmationMessage={showConfirmationMessage}
        setShowConfirmationMessage={setShowConfirmationMessage}
        setAddContactErrorMessage={setAddContactErrorMessage}
        isModalEditContactVisible={isModalEditContactVisible}
        setIsModalEditContactVisible={setIsModalEditContactVisible}
        customerContactList={customerContactList}
        setCustomerContactList={setCustomerContactList}
        setMaunallyCreateProject={setMaunallyCreateProject}
        data={projectData} 
        isLoading={isProjectLoading} // or false, since we have projectData
        error={projectError}
        selectedContactsIds={selectedContactsIds}
        setSelectedContactsIds={setSelectedContactsIds}
        queryClient={queryClient}
        navigate={navigate}
      />
    );
  } else if (!selectedProjectNumber && !maunallyCreateProject) {
    // Initial state, Briljant integration choice
    const IntegrationComponent = isByggkollenIntegration
      ? CreateProjectByggkollenIntegration
      : CreateProjectBriljantIntegration;
    formToRender = (
      <IntegrationComponent
        department={department}
        setDepartment={setDepartment}
        setSelectedProjectNumber={setSelectedProjectNumber}
        setErpReference={setErpReference}
      />
    );
  } else {
    // Fallback if no other condition met
    const IntegrationComponent = isByggkollenIntegration
      ? CreateProjectByggkollenIntegration
      : CreateProjectBriljantIntegration;
    formToRender = (
      <IntegrationComponent
        department={department}
        setDepartment={setDepartment}
        setSelectedProjectNumber={setSelectedProjectNumber}
        setErpReference={setErpReference}
      />
    );
  }

  return (
    <Container>
      {(!isProjectSelectionComplete || maunallyCreateProject) && (
        <>
          <h3 className="activeProjectsHeader create-project-header text-center mt-3 mb-5">
            Skapa projekt
          </h3>
          <Row className="create-project-mode-row d-flex justify-content-center align-items-center my-3">
            <Col className="d-flex justify-content-center">
              <Form>
                <div className="hstack create-project-mode-switch">
                  <Form.Label className="pe-2">Importera</Form.Label>
                  <Form.Check
                    type="switch"
                    id="create-project-mode-switch"
                    onChange={handleCreateModeSwitchChange}
                    checked={maunallyCreateProject}
                  />
                  <Form.Label>Manuellt</Form.Label>
                </div>
              </Form>
            </Col>
          </Row>
        </>
      )}
      {isModalAddContactVisible && (
        <Row>
          <ModalAddContact
            customerContactList={customerContactList}
            setCustomerContactList={setCustomerContactList}
            setIsModalAddContactVisible={setIsModalAddContactVisible}
            customerNumber={customerNumber}
            isModalAddContactVisible={isModalAddContactVisible}
            setIsContactListUpdated={setIsContactListUpdated}
            setAddContactErrorMessage={setAddContactErrorMessage}
            setShowConfirmationMessage={setShowConfirmationMessage}
            selectedContactsIds={selectedContactsIds} 
            setSelectedContactsIds={setSelectedContactsIds} 
          />
        </Row>
      )}
      {isModalEditContactVisible && (
        <Row>
          <ModalEditContact
            customerContactList={customerContactList}
            customerNumber={customerNumber}
            setCustomerContactList={setCustomerContactList}
            isModalEditContactVisible={isModalEditContactVisible}
            setIsModalEditContactVisible={setIsModalEditContactVisible}
            setIsContactListUpdated={setIsContactListUpdated}
            selectedContactsIds={selectedContactsIds} 
            setSelectedContactsIds={setSelectedContactsIds} 
          />
        </Row>
      )}
      <Row>
        <Col>
          {formToRender}
        </Col>
      </Row>
    </Container>
  );
};

export default CreateProject;